/**
 * Career Comparison engine: salary progression, cash flows, lifetime totals, break-even,
 * sensitivity and opportunity cost. Pure functions, no React, so every number is unit tested.
 *
 * Model (decided with Tyler, 20-Sep-2026):
 * - Everything is in today's dollars. Raises are above inflation; salaries stay flat in real
 *   terms once the 10-year growth table ends; tax brackets are fixed 2025 amounts.
 * - Year 1 earns the typed salary; the Year N raise sets the Year N+1 salary.
 * - Job-search months after school pro-rate the first working year.
 * - Only bonuses are taxed. Employer RRSP/401(k) match, health, HSA and the legacy single
 *   "Annual Benefits" figure are treated as after-tax value.
 * - Canada: 15% federal tuition credit on school costs, carried forward until used.
 * - Lifetime totals are sums of the year-by-year cash flows, so every tab agrees.
 */
import { US_STATE_TAX_RATES } from '../../utils/usStateTaxRates';
import { calculateCanadianTax, calculateUSTax, canadianTaxBreakdown } from './careerTax';
import {
  CareerPathData,
  CareerCashFlow,
  CareerTotals,
  CareerCumulativePoint,
  BreakEvenPoint,
  CareerComparisonSummary,
  SensitivityScenario,
  OpportunityCost,
  SalaryYear
} from '../../types/careerComparison';

/** Years the user can type an explicit raise for; later years stay flat in real dollars. */
export const GROWTH_TABLE_YEARS = 10;
/** Federal tuition tax credit rate (Canada). Provincial tuition credits vary and are ignored. */
export const TUITION_CREDIT_RATE = 0.15;

export const isUSLocation = (location: string): boolean =>
  location.length === 2 && Boolean(US_STATE_TAX_RATES[location]);

const usesUSTax = (career: CareerPathData, isUSA: boolean): boolean => isUSLocation(career.province) || isUSA;

/** Total tax (income tax + payroll) for one year. `isUSA` is the site country toggle. */
export const calculateTax = (income: number, location: string, isUSA: boolean): number =>
  isUSLocation(location) || isUSA ? calculateUSTax(income, location) : calculateCanadianTax(income, location);

const jobSearchYears = (career: CareerPathData): number =>
  career.hasEducation ? Math.floor(career.monthsAfterSchoolToStartJob / 12) : 0;

/** Share of the first working year actually worked (a 3-month search leaves 0.75). */
export const firstWorkingYearFraction = (career: CareerPathData): number =>
  career.hasEducation ? 1 - (career.monthsAfterSchoolToStartJob % 12) / 12 : 1;

export const firstWorkingAge = (career: CareerPathData): number =>
  career.hasEducation ? career.currentAge + career.yearsInSchool + jobSearchYears(career) : career.currentAge;

/** Full-year salary for each working year. Year 1 is the typed salary; raise N applies to year N+1. */
export const calculateSalaryProgression = (career: CareerPathData): SalaryYear[] => {
  const startingSalary = career.hasEducation ? career.expectedStartingSalary : career.currentSalary;
  const startingAge = firstWorkingAge(career);
  const progression: SalaryYear[] = [];
  let salary = startingSalary;

  for (let year = 0; startingAge + year < career.retirementAge; year++) {
    progression.push({ age: startingAge + year, salary: Math.round(salary) });
    if (year < GROWTH_TABLE_YEARS) {
      if (career.salaryGrowth.type === 'percentage') {
        salary *= 1 + (career.salaryGrowth.percentageGrowth[year] || 0) / 100;
      } else if (career.salaryGrowth.type === 'dollar') {
        salary += career.salaryGrowth.dollarGrowth[year] || 0;
      }
    }
  }
  return progression;
};

export interface BenefitsSplit {
  taxable: number;
  nonTaxable: number;
}

/** Benefits for one full working year. Only the bonus is taxable income. */
export const splitBenefits = (career: CareerPathData, salary: number, yearIndex: number): BenefitsSplit => {
  if (career.benefitsBreakdown) {
    const b = career.benefitsBreakdown;
    return {
      taxable: salary * (b.bonusPercent / 100),
      nonTaxable: salary * (b.rrspMatchPercent / 100) + b.healthInsurance + b.hsaAmount + b.profDevBudget + b.otherFixed
    };
  }
  return {
    taxable: 0,
    nonTaxable: career.nonSalaryBenefits * Math.pow(1 + career.benefitsGrowthRate / 100, yearIndex)
  };
};

export const calculateBenefits = (career: CareerPathData, salary: number, yearIndex: number): number => {
  const split = splitBenefits(career, salary, yearIndex);
  return split.taxable + split.nonTaxable;
};

export const buildCashFlows = (career: CareerPathData, isUSA: boolean): CareerCashFlow[] => {
  const cashFlows: CareerCashFlow[] = [];
  const tuitionCreditApplies = career.hasEducation && !usesUSTax(career, isUSA);
  let openingBalance = 0;
  let tuitionCreditAvailable = 0;

  /**
   * Tax for one year, less any tuition credit still banked. The credit reduces federal
   * tax only, so CPP and EI can never be wiped out by it.
   */
  const taxForYear = (income: number): number => {
    if (!tuitionCreditApplies) return calculateTax(income, career.province, isUSA);
    const breakdown = canadianTaxBreakdown(income, career.province);
    const used = Math.min(tuitionCreditAvailable, breakdown.federal);
    tuitionCreditAvailable -= used;
    return breakdown.total - used;
  };

  const pushRow = (row: Omit<CareerCashFlow, 'year' | 'openingBalance' | 'netIncome' | 'closingBalance'>) => {
    const netIncome = row.grossEarnings - row.taxes - row.educationCosts;
    cashFlows.push({
      ...row,
      year: cashFlows.length + 1,
      openingBalance,
      netIncome,
      closingBalance: openingBalance + netIncome
    });
    openingBalance += netIncome;
  };

  if (career.hasEducation) {
    for (let year = 0; year < career.yearsInSchool; year++) {
      if (tuitionCreditApplies) tuitionCreditAvailable += career.annualSchoolCost * TUITION_CREDIT_RATE;
      pushRow({
        age: career.currentAge + year,
        period: 'Education',
        grossEarnings: career.incomeDuringSchool,
        benefits: 0,
        taxes: taxForYear(career.incomeDuringSchool),
        educationCosts: career.annualSchoolCost
      });
    }
    for (let year = 0; year < jobSearchYears(career); year++) {
      pushRow({
        age: career.currentAge + career.yearsInSchool + year,
        period: 'Job Search',
        grossEarnings: 0,
        benefits: 0,
        taxes: 0,
        educationCosts: 0
      });
    }
  }

  calculateSalaryProgression(career).forEach(({ age, salary }, index) => {
    const fraction = index === 0 ? firstWorkingYearFraction(career) : 1;
    const split = splitBenefits(career, salary, index);
    const taxableIncome = (salary + split.taxable) * fraction;
    const benefits = (split.taxable + split.nonTaxable) * fraction;
    pushRow({
      age,
      period: 'Working',
      grossEarnings: salary * fraction + benefits,
      benefits,
      taxes: taxForYear(taxableIncome),
      educationCosts: 0
    });
  });

  return cashFlows;
};

export const buildCumulativeNetIncome = (career: CareerPathData, isUSA: boolean): CareerCumulativePoint[] => {
  let cumulativeNetIncome = 0;
  return buildCashFlows(career, isUSA).map(flow => {
    cumulativeNetIncome += flow.netIncome;
    return {
      year: flow.year,
      age: flow.age,
      period: flow.period,
      annualNetIncome: flow.netIncome,
      cumulativeNetIncome
    };
  });
};

/** Lifetime totals are sums of the cash flows, so the Summary can never disagree with the tables. */
export const calculateTotalEarnings = (career: CareerPathData, isUSA: boolean): CareerTotals => {
  const flows = buildCashFlows(career, isUSA);
  const sum = (pick: (flow: CareerCashFlow) => number) => flows.reduce((total, flow) => total + pick(flow), 0);
  const educationIncome = sum(flow => (flow.period === 'Education' ? flow.grossEarnings : 0));
  const educationCosts = sum(flow => flow.educationCosts);
  const workingYears = flows.filter(flow => flow.period === 'Working').length;
  const beforeTax = sum(flow => flow.grossEarnings) - educationCosts;
  const afterTax = sum(flow => flow.netIncome);

  return {
    beforeTax,
    afterTax,
    taxes: sum(flow => flow.taxes),
    benefits: sum(flow => flow.benefits),
    netEducationCost: educationCosts - educationIncome,
    workingYears,
    averageAnnualBeforeTax: workingYears > 0 ? beforeTax / workingYears : 0,
    averageAnnualAfterTax: workingYears > 0 ? afterTax / workingYears : 0
  };
};

/**
 * First age at which Path B's cumulative net income reaches Path A's, compared age by age.
 * Returns null if B never catches up; `aheadFromStart` when B leads at the first shared age.
 */
export const findBreakEven = (pathA: CareerPathData, pathB: CareerPathData, isUSA: boolean): BreakEvenPoint | null => {
  const pathAByAge = new Map(buildCumulativeNetIncome(pathA, isUSA).map(point => [point.age, point]));
  const pathBPoints = buildCumulativeNetIncome(pathB, isUSA).filter(point => pathAByAge.has(point.age));

  for (let i = 0; i < pathBPoints.length; i++) {
    const pointB = pathBPoints[i];
    const pointA = pathAByAge.get(pointB.age)!;
    if (pointB.cumulativeNetIncome >= pointA.cumulativeNetIncome) {
      return {
        year: pointB.year,
        age: pointB.age,
        aheadFromStart: i === 0,
        pathACumulative: pointA.cumulativeNetIncome,
        pathBCumulative: pointB.cumulativeNetIncome
      };
    }
  }
  return null;
};

export const compareCareers = (pathA: CareerPathData, pathB: CareerPathData, isUSA: boolean): CareerComparisonSummary => {
  const pathAData = calculateTotalEarnings(pathA, isUSA);
  const pathBData = calculateTotalEarnings(pathB, isUSA);
  const breakEven = findBreakEven(pathA, pathB, isUSA);
  const difference = pathBData.afterTax - pathAData.afterTax;
  const hasCrossover = breakEven !== null && !breakEven.aheadFromStart;

  return {
    pathA: pathAData,
    pathB: pathBData,
    difference,
    winner: difference > 0 ? 'B' : difference < 0 ? 'A' : 'Tie',
    breakevenYear: hasCrossover ? breakEven!.year : 0,
    breakevenAge: hasCrossover ? breakEven!.age : 0,
    aheadFromStart: breakEven?.aheadFromStart ?? false,
    pathAName: pathA.scenarioName || (pathA.hasEducation ? 'Career Path A (with Education)' : 'Career Path A (Direct)'),
    pathBName: pathB.scenarioName || (pathB.hasEducation ? 'Career Path B (with Education)' : 'Career Path B (Direct)')
  };
};

const SENSITIVITY_MULTIPLIERS = [
  { label: 'Pessimistic (50% of estimate)', multiplier: 0.5 },
  { label: 'Conservative (70% of estimate)', multiplier: 0.7 },
  { label: 'Your Estimate (100%)', multiplier: 1.0 },
  { label: 'Optimistic (150% of estimate)', multiplier: 1.5 }
];

/** Re-runs the comparison with Path B's raises scaled; break-even uses the same cumulative method as the Summary. */
export const calculateSensitivity = (
  pathA: CareerPathData,
  pathB: CareerPathData,
  baseBreakevenYear: number,
  isUSA: boolean
): SensitivityScenario[] => {
  return SENSITIVITY_MULTIPLIERS.map(({ label, multiplier }) => {
    const adjustedPathB: CareerPathData = {
      ...pathB,
      salaryGrowth: {
        ...pathB.salaryGrowth,
        percentageGrowth: pathB.salaryGrowth.percentageGrowth.map(rate => rate * multiplier),
        dollarGrowth: pathB.salaryGrowth.dollarGrowth.map(amount => amount * multiplier)
      }
    };
    const comparison = compareCareers(pathA, adjustedPathB, isUSA);

    let impact: string;
    if (multiplier === 1.0) {
      impact = 'Your base estimate';
    } else if (comparison.aheadFromStart) {
      impact = 'Path B ahead from the start';
    } else if (comparison.breakevenYear === 0) {
      impact = 'Path A leads throughout';
    } else if (comparison.breakevenYear > baseBreakevenYear && baseBreakevenYear > 0) {
      const yearsDiff = comparison.breakevenYear - baseBreakevenYear;
      impact = `${yearsDiff} year${yearsDiff > 1 ? 's' : ''} later than expected`;
    } else if (comparison.breakevenYear < baseBreakevenYear) {
      const yearsDiff = baseBreakevenYear - comparison.breakevenYear;
      impact = `${yearsDiff} year${yearsDiff > 1 ? 's' : ''} earlier than expected`;
    } else {
      impact = 'Same as base estimate';
    }

    return {
      label,
      multiplier,
      breakevenYear: comparison.breakevenYear,
      breakevenAge: comparison.breakevenAge,
      lifetimeDifference: comparison.difference,
      impact
    };
  });
};

/** What the salary given up in each school year would have grown to by retirement if invested instead. */
export const calculateOpportunityCost = (
  pathA: CareerPathData,
  educationPath: CareerPathData,
  investmentReturnRate: number
): OpportunityCost => {
  if (!educationPath.hasEducation) {
    return { totalForegoneSalary: 0, futureValueIfInvested: 0, opportunityCost: 0 };
  }
  const foregonePerYear = Math.max(0, pathA.currentSalary - educationPath.incomeDuringSchool);
  const totalForegoneSalary = foregonePerYear * educationPath.yearsInSchool;
  const yearsToRetirement = educationPath.retirementAge - (educationPath.currentAge + educationPath.yearsInSchool);
  const rate = investmentReturnRate / 100;
  let futureValueIfInvested = 0;
  for (let year = 0; year < educationPath.yearsInSchool; year++) {
    const yearsInvested = Math.max(0, educationPath.retirementAge - (educationPath.currentAge + year + 1));
    futureValueIfInvested += foregonePerYear * Math.pow(1 + rate, yearsInvested);
  }
  return {
    totalForegoneSalary,
    futureValueIfInvested,
    opportunityCost: futureValueIfInvested - totalForegoneSalary,
    yearsToRetirement,
    returnRate: investmentReturnRate
  };
};
